import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplyPsychoPricesDialog } from '@/components/apply-psycho-prices-dialog';
import { RemoveInvalidGtinsDialog } from '@/components/remove-invalid-gtins-dialog';
import { Sidebar } from '@/components/ui-v2/sidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  DatasourceInstructionUpdateField,
  ExternalCatalogConnectionCms,
  ProductType,
  PsychologicalPriceStrategy,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { useApplyPsychoPrices } from '@/lib/hooks/useApplyPsychoPrices';
import { useExecuteOptimisticUpdate } from '@/lib/hooks/useExecuteOptimisticUpdate';
import { useExtractColorFromImages } from '@/lib/hooks/useExtractColorFromImages';
import { useShopifyFieldSync } from '@/lib/hooks/useShopifyFieldSync';
import { useTriggerCategorizeDealItems } from '@/lib/hooks/useTriggerCategorizeDealItems';
import { useUpdateDealItems } from '@/lib/hooks/useUpdateDealItems';
import { trackEvent } from '@/lib/mixpanel';
import { cn, getVatLabelKey } from '@/lib/utils';
import { toggleUpdatingField } from '@/lib/utils/updating-fields';
import { translateFieldNames } from '@/lib/utils/validation-error-translator';
import {
  blockingAnomalyTypesCountAtom,
  toCheckAnomalyTypesCountAtom,
} from '@/states/anomaly-count.state';
import {
  RULES_WITH_DEDICATED_SECTION,
  blockingValidationErrorsAtom,
  missingRequiredFieldLabelsAtom,
} from '@/states/blocking-validation-errors.state';
import {
  ColumnFilterId,
  useSetColumnFilter,
} from '@/states/column-filters.state';
import {
  DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
  filterSidebarAccordionDefaultValueState,
} from '@/states/filter-sidebar-accordion.state';
import { hasDigitalProductsWithoutCouponCodesAtom } from '@/states/has-digital-products-without-coupon-codes.state';
import { openImportCouponCodesModalAtom } from '@/states/is-sidebar-alert-open.state';
import {
  duplicateVariantManualItemIdsAtom,
  duplicateVariantShopifyItemIdsAtom,
  duplicateVariantShopifyWithImagesItemIdsAtom,
  duplicateVariantWithImagesItemIdsAtom,
  hasDuplicateVariantFixAtom,
  hasShopifyOriginImageFallbackDuplicateAtom,
  invalidGtinObjectItemIdsAtom,
  missingCategoryObjectItemIdsAtom,
  missingShippingObjectItemIdsAtom,
} from '@/states/object-anomalies.state';
import { NO_CATEGORY_FILTER_KEY } from '@/states/product-category-filter.state';
import { productsMapState, productsState } from '@/states/products.state';
import { psychologicalPriceErrorItemIdsAtom } from '@/states/psychological-price-errors.state';
import { scrollToTableColumnIdState } from '@/states/scroll-to-table-column.state';
import { pendingShopifyCategoryFallbackAtom } from '@/states/shopify-category-fallback.state';
import { shopifyVariantRecoveryAttemptedDealIdsAtom } from '@/states/shopify-variant-recovery.state';
import { storeAtom } from '@/states/store.state';
import { vatRatesAtom } from '@/states/vat-rates.state';
import { ColumnAccessor } from '@/types/columns';
import { Currency } from '@/types/currency';
import { Store } from '@/types/store';
import { useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, AlertTriangle, ChevronDown, Loader2 } from 'lucide-react';

import { Button } from './ui/button';

interface AlertSidebarProps {
  onClose: () => void;
}

const RULE_ID_TO_COLUMN: Partial<Record<ValidationRuleId, ColumnAccessor>> = {
  [ValidationRuleId.DiscountConsistency]: ColumnAccessor.chooseDiscount,
  [ValidationRuleId.DuplicateGtin]: ColumnAccessor.gtin,
  [ValidationRuleId.GtinValidness]: ColumnAccessor.gtin,
  [ValidationRuleId.DuplicateSku]: ColumnAccessor.sku,
  [ValidationRuleId.SkuOrGtinOrShopifyVariantId]: ColumnAccessor.sku,
  [ValidationRuleId.NegativeMargin]: ColumnAccessor.marginPercentage,
  [ValidationRuleId.PositiveStock]: ColumnAccessor.stockQuantity,
  [ValidationRuleId.PriceCompetitiveness]: ColumnAccessor.choosePrice,
  [ValidationRuleId.UrlOrDescriptionImagesOrDriveLink]:
    ColumnAccessor.sourceUrl,
};

type Severity = 'error' | 'warning';

/**
 * Collapsible per-anomaly section: a trigger row (severity icon + title, plus a
 * colored product-count badge and a chevron on the right) over collapsible
 * content (optional description + the action card passed as children).
 *
 * The content stays mounted and is hidden via a `grid-template-rows: 0fr↔1fr`
 * transition, so form state (e.g. the shipping input) survives a collapse. While
 * collapsed the content is made `inert` (removed from tab order and a11y tree)
 * without cutting the animation. When expanded the layout matches the previous
 * static section (title → description gap, then the card). The badge is a color
 * reminder of the group severity and only shows while collapsed.
 */
function AnomalyCollapsible({
  id,
  title,
  description,
  count,
  open,
  onToggle,
  severity = 'error',
  dataTestId,
  children,
}: {
  id: string;
  title: string;
  description?: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  severity?: Severity;
  dataTestId?: string;
  children: React.ReactNode;
}) {
  const isWarn = severity === 'warning';
  const Icon = isWarn ? AlertTriangle : AlertCircle;
  const iconColor = isWarn ? 'text-orange-500' : 'text-red-500';

  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [open]);

  return (
    <div className="pb-6" data-testid={dataTestId}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`alert-sidebar-collapsible-${id}`}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <Icon className={cn('size-3.5 shrink-0', iconColor)} />
        <span className="text-[15px] font-medium text-gray-900">{title}</span>
        <span className="ml-auto flex items-center gap-2">
          {!open && (
            <span
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                isWarn
                  ? 'border-orange-200 bg-orange-50 text-orange-500'
                  : 'border-red-200 bg-red-50 text-red-500'
              )}
            >
              {count}
            </span>
          )}
          <ChevronDown
            className={cn(
              'size-4 shrink-0 text-gray-400 transition-transform',
              open && 'rotate-180'
            )}
          />
        </span>
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div ref={contentRef} className="overflow-hidden">
          {description ? (
            <p className="pb-2 pt-1 text-xs text-gray-500">{description}</p>
          ) : (
            <div className="pt-2" />
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Section separator introducing a severity group ("À corriger avant validation"
 * / "À vérifier"): pill icon + colored title + "N types" badge + description.
 * The root carries a highlight ring when its counter tile is clicked.
 */
function AnomalyGroupHeader({
  severity,
  title,
  description,
  headerRef,
  highlighted,
}: {
  severity: Severity;
  title: string;
  description: string;
  headerRef?: React.Ref<HTMLDivElement>;
  highlighted?: boolean;
}) {
  const isWarn = severity === 'warning';
  const Icon = isWarn ? AlertTriangle : AlertCircle;
  return (
    <div
      ref={headerRef}
      className={cn(
        'mb-4 rounded-md transition-shadow',
        highlighted &&
          (isWarn ? 'ring-2 ring-orange-300' : 'ring-2 ring-red-300')
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2.5 border-b-2 pb-2.5',
          isWarn ? 'border-orange-200' : 'border-red-200'
        )}
      >
        <span
          className={cn(
            'inline-flex size-7 items-center justify-center rounded-full',
            isWarn ? 'bg-orange-50' : 'bg-red-50'
          )}
        >
          <Icon
            className={cn(
              'size-3.5',
              isWarn ? 'text-orange-500' : 'text-red-500'
            )}
          />
        </span>
        <p
          className={cn(
            'text-[15px] font-semibold',
            isWarn ? 'text-orange-500' : 'text-red-500'
          )}
        >
          {title}
        </p>
      </div>
      <p className="mt-1.5 text-[13px] text-gray-500">{description}</p>
    </div>
  );
}

/**
 * Clickable severity counter tile shown under the sidebar header. Displays the
 * number of anomaly **types** in its group and scrolls to the matching section.
 */
function AnomalyCountTile({
  severity,
  count,
  onClick,
}: {
  severity: Severity;
  count: number;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const isWarn = severity === 'warning';
  const Icon = isWarn ? AlertTriangle : AlertCircle;
  const color = isWarn ? 'text-orange-500' : 'text-red-500';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 cursor-pointer flex-col rounded-lg border p-3 text-left transition hover:brightness-95',
        isWarn ? 'border-orange-200 bg-orange-50' : 'border-red-200 bg-red-50'
      )}
    >
      <span
        className={cn('flex items-center gap-1.5 text-sm font-medium', color)}
      >
        <Icon className="size-3.5" />
        {t(
          isWarn
            ? 'components.alert_sidebar.tile_to_check'
            : 'components.alert_sidebar.tile_to_fix'
        )}
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className={cn('text-[26px] font-bold leading-tight', color)}>
          {count}
        </span>
        <span className={cn('text-xs font-bold', color)}>
          {t('components.alert_sidebar.tile_types_label', { count })}
        </span>
      </span>
      <span className="text-xs text-gray-400">
        {t(
          isWarn
            ? 'components.alert_sidebar.tile_types_to_check'
            : 'components.alert_sidebar.tile_types_blocking',
          { count }
        )}
      </span>
    </button>
  );
}

export const AlertSidebar: React.FC<AlertSidebarProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { vatRates, uniqueVatRates } = useAtomValue(vatRatesAtom);
  const hasDigitalProductsWithoutCoupons = useAtomValue(
    hasDigitalProductsWithoutCouponCodesAtom
  );
  const products = useAtomValue(productsState);
  const store = useAtomValue(storeAtom);
  const vatLabel = t(getVatLabelKey(store));

  const setFilterSidebarAccordionDefault = useSetAtom(
    filterSidebarAccordionDefaultValueState
  );
  const setScrollToColumnId = useSetAtom(scrollToTableColumnIdState);
  const { resetAndSetColumnFilter } = useSetColumnFilter();

  const handleVatRateCheck = useCallback(
    (rate: number) => {
      const categoryIds = new Set<string>();
      let includeNoCategory = false;
      for (const product of products) {
        if (
          !product.activeVersion.isSelected ||
          product.activeVersion.vatRate !== rate
        )
          continue;
        const id = product.activeVersion.productCategory?.productCategoryId;
        if (id) categoryIds.add(id);
        else includeNoCategory = true;
      }
      const nextValue = [...categoryIds].sort((a, b) => a.localeCompare(b));
      if (includeNoCategory) nextValue.push(NO_CATEGORY_FILTER_KEY);

      resetAndSetColumnFilter(ColumnFilterId.productCategory, nextValue);

      setFilterSidebarAccordionDefault([
        ...DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
        'categories',
      ]);
      setScrollToColumnId('vatRate');
      onClose();
    },
    [
      products,
      resetAndSetColumnFilter,
      setFilterSidebarAccordionDefault,
      onClose,
      setScrollToColumnId,
    ]
  );

  const countProductsByVatRate = useMemo(() => {
    const counts: Record<number, number> = {};
    for (const rate of vatRates) {
      counts[rate] = (counts[rate] ?? 0) + 1;
    }
    return counts;
  }, [vatRates]);

  const digitalProductsCount = useMemo(() => {
    let count = 0;
    for (const product of products) {
      if (
        product.activeVersion.isSelected &&
        product.activeVersion.productType === ProductType.Coupon
      ) {
        count += 1;
      }
    }
    return count;
  }, [products]);

  const setOpenImportCouponCodesModal = useSetAtom(
    openImportCouponCodesModalAtom
  );

  const dealId = useDealContext().deal.id;
  const { toast } = useToast();
  const { executeOptimisticUpdate } = useExecuteOptimisticUpdate();

  // Category & shipping fixes are internal-only actions (hidden for brands).
  const isInternalUser = !!useUser()?.isInternalUser;

  // --- Missing category, physical products only (front anomaly) ---
  const missingCategoryIds = useAtomValue(missingCategoryObjectItemIdsAtom);
  const { triggerCategorizeDealItems } = useTriggerCategorizeDealItems(dealId);
  const [isCategorizing, setIsCategorizing] = useState(false);

  // Prefer the Shopify datasource when a Shopify catalog is connected;
  // AI categorization is the fallback otherwise.
  const hasShopifyConnection =
    useDealContext().deal.externalCatalogConnections.some(
      (connection) =>
        connection.cms === ExternalCatalogConnectionCms.Shopify &&
        connection.isActive
    );
  const { sync: syncShopifyCategory, loading: syncingShopifyCategory } =
    useShopifyFieldSync(dealId);
  const setProductsMap = useSetAtom(productsMapState);
  const setPendingShopifyCategoryFallback = useSetAtom(
    pendingShopifyCategoryFallbackAtom
  );

  const handleCategorize = useCallback(async () => {
    if (
      missingCategoryIds.length === 0 ||
      isCategorizing ||
      syncingShopifyCategory
    )
      return;

    // Shopify connected → update the category from the Shopify datasource,
    // then AI-categorize the items Shopify could not resolve.
    if (hasShopifyConnection) {
      trackEvent('ANOMALY_FIX_CLICKED', {
        dealId,
        anomalyType: 'missing_category',
        method: 'shopify_datasource',
        itemCount: missingCategoryIds.length,
      });

      const idsSet = new Set(missingCategoryIds);
      setProductsMap((prev) =>
        toggleUpdatingField(prev, idsSet, 'productCategory', true)
      );

      setPendingShopifyCategoryFallback({
        dealId,
        itemIds: missingCategoryIds,
      });

      await syncShopifyCategory({
        dealItemsIds: missingCategoryIds,
        fields: [DatasourceInstructionUpdateField.ProductCategory],
        inProgressMessage: t(
          'components.alert_sidebar.category_sync_in_progress'
        ),
        // Close the sidebar once the datasource is launched so the action
        // can't be re-triggered while it runs in the background.
        onSuccess: () => onClose(),
      });
      return;
    }

    // Fallback → AI categorization.
    const count = missingCategoryIds.length;
    trackEvent('ANOMALY_FIX_CLICKED', {
      dealId,
      anomalyType: 'missing_category',
      method: 'ai_categorize',
      itemCount: count,
    });
    setIsCategorizing(true);
    try {
      await executeOptimisticUpdate({
        itemIds: missingCategoryIds,
        optimisticUpdate: (prev) => prev,
        mutation: () => triggerCategorizeDealItems(missingCategoryIds, false),
        checkSuccess: (result) =>
          result.data?.triggerCategorizeDealItems?.__typename ===
          'MutationSuccess',
        getErrorMessage: (result) =>
          result.data?.triggerCategorizeDealItems?.__typename ===
          'DomainException'
            ? result.data.triggerCategorizeDealItems.code
            : t('general.error_categorize_deal_items'),
        defaultErrorMessage: t('general.error_categorize_deal_items'),
        errorContext: 'AlertSidebar.handleCategorize',
        inProgressMessage: t('general.categorization_in_progress'),
        onSuccess: () => {
          toast({
            title: t('general.success'),
            description: t('general.success_categorize_deal_items', { count }),
          });
          // Close once the job is launched so it can't be re-triggered while
          // it runs (the section stays until the subscription clears it).
          onClose();
        },
      });
    } finally {
      setIsCategorizing(false);
    }
  }, [
    dealId,
    missingCategoryIds,
    isCategorizing,
    syncingShopifyCategory,
    hasShopifyConnection,
    syncShopifyCategory,
    setProductsMap,
    setPendingShopifyCategoryFallback,
    executeOptimisticUpdate,
    triggerCategorizeDealItems,
    t,
    toast,
    onClose,
  ]);

  // Filter the table to the uncategorized products (like the VAT alert).
  const handleFilterMissingCategory = useCallback(() => {
    resetAndSetColumnFilter(ColumnFilterId.productCategory, [
      NO_CATEGORY_FILTER_KEY,
    ]);
    setFilterSidebarAccordionDefault([
      ...DEFAULT_FILTER_SIDEBAR_ACCORDION_VALUES,
      'categories',
    ]);
    setScrollToColumnId('productCategory');
    onClose();
  }, [
    resetAndSetColumnFilter,
    setFilterSidebarAccordionDefault,
    setScrollToColumnId,
    onClose,
  ]);

  // --- Missing shipping price, physical products only (front anomaly) ---
  const missingShippingIds = useAtomValue(missingShippingObjectItemIdsAtom);
  const { updateProducts } = useUpdateDealItems(dealId);
  const [shippingValue, setShippingValue] = useState('');
  const [isApplyingShipping, setIsApplyingShipping] = useState(false);

  const parsedShippingAmount = Number(shippingValue.replace(',', '.'));
  const isShippingValueValid =
    shippingValue.trim() !== '' &&
    Number.isFinite(parsedShippingAmount) &&
    parsedShippingAmount >= 0;

  const handleApplyShipping = useCallback(async () => {
    if (missingShippingIds.length === 0 || isApplyingShipping) return;
    const amount = Number(shippingValue.replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0) return;
    const count = missingShippingIds.length;
    const cents = Math.round(amount * 100);
    const targetIds = new Set(missingShippingIds);
    trackEvent('ANOMALY_FIX_CLICKED', {
      dealId,
      anomalyType: 'missing_shipping',
      method: 'bulk_update',
      itemCount: count,
    });
    setIsApplyingShipping(true);
    try {
      const success = await executeOptimisticUpdate({
        itemIds: missingShippingIds,
        // Set the price locally so the anomaly clears immediately; the
        // subscription confirms the server values afterwards.
        optimisticUpdate: (prev) =>
          prev.map((product) =>
            targetIds.has(product.id)
              ? {
                  ...product,
                  activeVersion: {
                    ...product.activeVersion,
                    shippingPrice: cents,
                  },
                }
              : product
          ),
        // updateProducts expects euros and converts to cents internally.
        mutation: () =>
          updateProducts(
            missingShippingIds.map((id) => ({ id, shippingPrice: amount }))
          ),
        checkSuccess: (result) =>
          result.data?.updateDealItemsV2?.__typename ===
          'MutationUpdateDealItemsSuccess',
        getErrorMessage: (result) =>
          result.data?.updateDealItemsV2?.__typename === 'DomainException'
            ? result.data.updateDealItemsV2.code
            : t('components.alert_sidebar.shipping_error'),
        defaultErrorMessage: t('components.alert_sidebar.shipping_error'),
        errorContext: 'AlertSidebar.handleApplyShipping',
        inProgressMessage: t('components.alert_sidebar.shipping_in_progress'),
        modifiedFields: ['shippingPrice'],
        onSuccess: () => {
          toast({
            title: t('general.success'),
            description: t('components.alert_sidebar.shipping_success', {
              count,
            }),
          });
        },
      });
      if (success) {
        setShippingValue('');
      }
    } finally {
      setIsApplyingShipping(false);
    }
  }, [
    dealId,
    missingShippingIds,
    isApplyingShipping,
    shippingValue,
    executeOptimisticUpdate,
    updateProducts,
    t,
    toast,
  ]);

  // --- Duplicate variants from Shopify (recover options from source) ---
  const duplicateVariantShopifyIds = useAtomValue(
    duplicateVariantShopifyItemIdsAtom
  );
  const { sync: syncShopifyVariants, loading: syncingShopifyVariants } =
    useShopifyFieldSync(dealId);

  // Session-only memory of the deals whose Shopify recovery was already
  // triggered. Once recovery has run, we additionally offer to extract the
  // options from the images for the Shopify duplicates it couldn't resolve.
  const recoveryAttemptedDealIds = useAtomValue(
    shopifyVariantRecoveryAttemptedDealIdsAtom
  );
  const setRecoveryAttemptedDealIds = useSetAtom(
    shopifyVariantRecoveryAttemptedDealIdsAtom
  );
  const hasAttemptedShopifyRecovery = recoveryAttemptedDealIds.has(dealId);

  const handleRecoverVariants = useCallback(async () => {
    if (duplicateVariantShopifyIds.length === 0 || syncingShopifyVariants) {
      return;
    }
    trackEvent('ANOMALY_FIX_CLICKED', {
      dealId,
      anomalyType: 'duplicate_variant',
      method: 'shopify_datasource',
      itemCount: duplicateVariantShopifyIds.length,
    });
    // Pull the variant options from the Shopify source; the filled options
    // lift the duplicate_variant error once the datasource is processed.
    await syncShopifyVariants({
      dealItemsIds: duplicateVariantShopifyIds,
      fields: [DatasourceInstructionUpdateField.Options],
      inProgressMessage: t(
        'components.alert_sidebar.variant_recover_in_progress'
      ),
      // Close the sidebar once the datasource is launched so the action
      // can't be re-triggered while it runs in the background.
      onSuccess: () => {
        // Remember (session-only) that recovery ran for this deal, so the
        // image-extraction fallback is proposed for any Shopify duplicates the
        // recovery leaves unresolved when the user reopens the sidebar.
        setRecoveryAttemptedDealIds((prev) => {
          if (prev.has(dealId)) return prev;
          const next = new Set(prev);
          next.add(dealId);
          return next;
        });
        onClose();
      },
    });
  }, [
    dealId,
    duplicateVariantShopifyIds,
    syncingShopifyVariants,
    syncShopifyVariants,
    setRecoveryAttemptedDealIds,
    t,
    onClose,
  ]);

  // Fallback for non-Shopify duplicates: extract options from the images.
  const duplicateVariantWithImagesIds = useAtomValue(
    duplicateVariantWithImagesItemIdsAtom
  );
  // Shopify duplicates that still carry images: eligible for image extraction
  // only once the Shopify recovery has already been attempted for this deal.
  const duplicateVariantShopifyWithImagesIds = useAtomValue(
    duplicateVariantShopifyWithImagesItemIdsAtom
  );
  const canExtractShopifyAfterRecovery =
    hasAttemptedShopifyRecovery &&
    duplicateVariantShopifyWithImagesIds.length > 0;
  // Once the Shopify recovery has been triggered for this deal we stop
  // re-proposing it: the user falls back to the image extraction instead.
  const canRecoverVariants =
    duplicateVariantShopifyIds.length > 0 && !hasAttemptedShopifyRecovery;
  // The two id lists are disjoint (Shopify-recoverable vs. not), so a plain
  // concatenation needs no de-duplication.
  const extractOptionsIds = useMemo(
    () =>
      canExtractShopifyAfterRecovery
        ? [
            ...duplicateVariantWithImagesIds,
            ...duplicateVariantShopifyWithImagesIds,
          ]
        : duplicateVariantWithImagesIds,
    [
      canExtractShopifyAfterRecovery,
      duplicateVariantWithImagesIds,
      duplicateVariantShopifyWithImagesIds,
    ]
  );
  const { extractColorFromImages } = useExtractColorFromImages(dealId);
  const [isExtractingOptions, setIsExtractingOptions] = useState(false);

  const handleExtractOptions = useCallback(async () => {
    if (extractOptionsIds.length === 0 || isExtractingOptions) {
      return;
    }
    const count = extractOptionsIds.length;
    trackEvent('ANOMALY_FIX_CLICKED', {
      dealId,
      anomalyType: 'duplicate_variant',
      method: 'extract_options',
      itemCount: count,
    });
    setIsExtractingOptions(true);
    try {
      await executeOptimisticUpdate({
        itemIds: extractOptionsIds,
        optimisticUpdate: (prev) => prev,
        mutation: () => extractColorFromImages(extractOptionsIds),
        checkSuccess: (result) =>
          result.data?.extractColorFromImages?.__typename === 'MutationSuccess',
        getErrorMessage: (result) =>
          result.data?.extractColorFromImages?.__typename === 'DomainException'
            ? result.data.extractColorFromImages.code
            : t('components.alert_sidebar.variant_extract_error'),
        defaultErrorMessage: t(
          'components.alert_sidebar.variant_extract_error'
        ),
        errorContext: 'AlertSidebar.handleExtractOptions',
        inProgressMessage: t(
          'components.alert_sidebar.variant_extract_in_progress'
        ),
        modifiedFields: ['options'],
        onSuccess: () => {
          toast({
            title: t('general.success'),
            description: t('components.alert_sidebar.variant_extract_success', {
              count,
            }),
          });
          // Close once the job is launched so it can't be re-triggered while
          // it runs (the section stays until the subscription clears it).
          onClose();
        },
      });
    } finally {
      setIsExtractingOptions(false);
    }
  }, [
    dealId,
    extractOptionsIds,
    isExtractingOptions,
    executeOptimisticUpdate,
    extractColorFromImages,
    t,
    toast,
    onClose,
  ]);

  const duplicateVariantManualIds = useAtomValue(
    duplicateVariantManualItemIdsAtom
  );
  const hasDuplicateVariantFix = useAtomValue(hasDuplicateVariantFixAtom);
  const hasShopifyOriginImageFallback = useAtomValue(
    hasShopifyOriginImageFallbackDuplicateAtom
  );
  // Once recovery has been attempted the Shopify CTA is gone, so the copy must
  // no longer tell the user to recover from Shopify: drive it off what's still
  // actionable (`canRecoverVariants` vs. the image extraction), reusing the
  // existing keys — no new wording.
  const variantDescriptionKey = canRecoverVariants
    ? duplicateVariantWithImagesIds.length > 0
      ? 'components.alert_sidebar.variant_description_mixed'
      : 'components.alert_sidebar.variant_description'
    : duplicateVariantWithImagesIds.length > 0 || canExtractShopifyAfterRecovery
      ? hasShopifyOriginImageFallback
        ? 'components.alert_sidebar.variant_description_shopify_inactive'
        : 'components.alert_sidebar.variant_description_images'
      : 'components.alert_sidebar.variant_description_manual';

  // Filter the table to the duplicate-variant products (like the VAT alert).
  const handleFilterDuplicateVariants = useCallback(() => {
    resetAndSetColumnFilter(ColumnFilterId.validationErrors, [
      ValidationRuleId.DuplicateVariant,
    ]);
    setScrollToColumnId('name');
    onClose();
  }, [resetAndSetColumnFilter, setScrollToColumnId, onClose]);

  const psychoPriceIds = useAtomValue(psychologicalPriceErrorItemIdsAtom);
  const { applyPsychoPrices: runApplyPsychoPrices } =
    useApplyPsychoPrices(dealId);
  const [showPsychoModal, setShowPsychoModal] = useState(false);
  const [isApplyingPsycho, setIsApplyingPsycho] = useState(false);

  const applyPsychoPrices = useCallback(
    async (strategy: PsychologicalPriceStrategy) => {
      setShowPsychoModal(false);
      if (psychoPriceIds.length === 0 || isApplyingPsycho) return;
      trackEvent('ANOMALY_FIX_CLICKED', {
        dealId,
        anomalyType: 'psychological_price',
        method: 'compute_choose_price',
        itemCount: psychoPriceIds.length,
      });
      setIsApplyingPsycho(true);
      try {
        await runApplyPsychoPrices({
          itemIds: psychoPriceIds,
          strategy,
          errorContext: 'AlertSidebar.applyPsychoPrices',
          onSuccess: onClose,
        });
      } finally {
        setIsApplyingPsycho(false);
      }
    },
    [dealId, psychoPriceIds, isApplyingPsycho, runApplyPsychoPrices, onClose]
  );

  const handleFilterPsychoPrices = useCallback(() => {
    resetAndSetColumnFilter(ColumnFilterId.validationErrors, [
      ValidationRuleId.PsychologicalPriceApplication,
    ]);
    onClose();
  }, [resetAndSetColumnFilter, onClose]);

  const invalidGtinIds = useAtomValue(invalidGtinObjectItemIdsAtom);
  const [isRemovingGtins, setIsRemovingGtins] = useState(false);
  const [showRemoveGtinDialog, setShowRemoveGtinDialog] = useState(false);

  const handleFilterInvalidGtins = useCallback(() => {
    resetAndSetColumnFilter(ColumnFilterId.validationErrors, [
      ValidationRuleId.GtinValidness,
    ]);
    setScrollToColumnId(ColumnAccessor.gtin);
    onClose();
  }, [resetAndSetColumnFilter, setScrollToColumnId, onClose]);

  const handleRemoveInvalidGtins = useCallback(async () => {
    if (invalidGtinIds.length === 0 || isRemovingGtins) return;
    const count = invalidGtinIds.length;
    const targetIds = new Set(invalidGtinIds);
    trackEvent('ANOMALY_FIX_CLICKED', {
      dealId,
      anomalyType: 'invalid_gtin',
      method: 'bulk_remove',
      itemCount: count,
    });
    setIsRemovingGtins(true);
    try {
      await executeOptimisticUpdate({
        itemIds: invalidGtinIds,
        optimisticUpdate: (prev) =>
          prev.map((product) =>
            targetIds.has(product.id)
              ? {
                  ...product,
                  activeVersion: { ...product.activeVersion, gtin: null },
                }
              : product
          ),
        mutation: () =>
          updateProducts(invalidGtinIds.map((id) => ({ id, gtin: null }))),
        checkSuccess: (result) =>
          result.data?.updateDealItemsV2?.__typename ===
          'MutationUpdateDealItemsSuccess',
        getErrorMessage: (result) =>
          result.data?.updateDealItemsV2?.__typename === 'DomainException'
            ? result.data.updateDealItemsV2.code
            : t('components.alert_sidebar.gtin_remove_error'),
        defaultErrorMessage: t('components.alert_sidebar.gtin_remove_error'),
        errorContext: 'AlertSidebar.handleRemoveInvalidGtins',
        inProgressMessage: t(
          'components.alert_sidebar.gtin_remove_in_progress'
        ),
        modifiedFields: ['gtin'],
        onSuccess: () => {
          toast({
            title: t('general.success'),
            description: t('components.alert_sidebar.gtin_remove_success', {
              count,
            }),
          });
        },
      });
    } finally {
      setIsRemovingGtins(false);
    }
  }, [
    dealId,
    invalidGtinIds,
    isRemovingGtins,
    executeOptimisticUpdate,
    updateProducts,
    t,
    toast,
  ]);

  const blockingErrors = useAtomValue(blockingValidationErrorsAtom);
  // Memoized so it stays referentially stable across renders — `allAnomalyIds`
  // depends on it, and it feeds the section map.
  const visibleBlockingErrors = useMemo(
    () =>
      blockingErrors.filter(
        (error) => !RULES_WITH_DEDICATED_SECTION.has(error.ruleId)
      ),
    [blockingErrors]
  );

  const handleFilterBlockingError = useCallback(
    (ruleId: ValidationRuleId) => {
      resetAndSetColumnFilter(ColumnFilterId.validationErrors, [ruleId]);
      setScrollToColumnId(RULE_ID_TO_COLUMN[ruleId] ?? ColumnAccessor.name);
      onClose();
    },
    [resetAndSetColumnFilter, setScrollToColumnId, onClose]
  );

  // Filter the table to the products missing a shipping price (like the others).
  const handleFilterMissingShipping = useCallback(() => {
    resetAndSetColumnFilter(ColumnFilterId.validationErrors, [
      ValidationRuleId.RequiredShippingFees,
    ]);
    setScrollToColumnId(ColumnAccessor.shippingPrice);
    onClose();
  }, [resetAndSetColumnFilter, setScrollToColumnId, onClose]);

  // Distinct missing field labels for the "Données manquantes" card description.
  const missingFieldLabels = useAtomValue(missingRequiredFieldLabelsAtom);
  const blockingErrorDescription = useCallback(
    (ruleId: ValidationRuleId): string | undefined => {
      if (
        ruleId === ValidationRuleId.RequiredFields &&
        missingFieldLabels.length > 0
      ) {
        return t('components.alert_sidebar.required_fields_description', {
          fields: translateFieldNames(
            missingFieldLabels.join(', '),
            t,
            i18n.language
          ),
        });
      }
      return (
        t(`validation_errors.${ruleId}.fixSuggestion`, { defaultValue: '' }) ||
        undefined
      );
    },
    [missingFieldLabels, t, i18n.language]
  );

  // Severity type counters (types, not products) shared with the banner.
  const blockingTypesCount = useAtomValue(blockingAnomalyTypesCountAtom);
  const toCheckTypesCount = useAtomValue(toCheckAnomalyTypesCountAtom);

  const hasBlockingContent =
    isInternalUser &&
    (visibleBlockingErrors.length > 0 ||
      missingCategoryIds.length > 0 ||
      missingShippingIds.length > 0 ||
      hasDuplicateVariantFix ||
      hasDigitalProductsWithoutCoupons);

  const hasToCheckContent =
    isInternalUser &&
    (psychoPriceIds.length > 0 ||
      invalidGtinIds.length > 0 ||
      uniqueVatRates.length > 1);

  // --- Collapsible sections (all collapsed on open) ---
  // The sidebar is mounted fresh each time it opens, so this resets to "all
  // collapsed" every open, like `highlightedGroup` below.
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleId = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Ids of the sections actually rendered, mirroring the render conditions.
  const allAnomalyIds = useMemo(() => {
    const ids: string[] = [];
    if (hasBlockingContent) {
      for (const error of visibleBlockingErrors) {
        ids.push(`blocking-error-${error.ruleId}`);
      }
      if (missingCategoryIds.length > 0) ids.push('missing-category');
      if (missingShippingIds.length > 0) ids.push('missing-shipping');
      if (hasDuplicateVariantFix) ids.push('duplicate-variant');
      if (hasDigitalProductsWithoutCoupons) ids.push('missing-coupons');
    }
    if (hasToCheckContent) {
      if (psychoPriceIds.length > 0) ids.push('psycho');
      if (invalidGtinIds.length > 0) ids.push('invalid-gtin');
      if (uniqueVatRates.length > 1) ids.push('vat-rates');
    }
    return ids;
  }, [
    hasBlockingContent,
    hasToCheckContent,
    visibleBlockingErrors,
    missingCategoryIds.length,
    missingShippingIds.length,
    hasDuplicateVariantFix,
    hasDigitalProductsWithoutCoupons,
    psychoPriceIds.length,
    invalidGtinIds.length,
    uniqueVatRates.length,
  ]);

  const allOpen =
    allAnomalyIds.length > 0 && allAnomalyIds.every((id) => openIds.has(id));
  const toggleAll = useCallback(() => {
    setOpenIds((prev) =>
      allAnomalyIds.length > 0 && allAnomalyIds.every((id) => prev.has(id))
        ? new Set()
        : new Set(allAnomalyIds)
    );
  }, [allAnomalyIds]);

  // Badge count for the VAT card
  const vatTotalCount = useMemo(
    () =>
      uniqueVatRates.reduce(
        (sum, rate) => sum + (countProductsByVatRate[rate] ?? 0),
        0
      ),
    [uniqueVatRates, countProductsByVatRate]
  );

  // Tile click → smooth-scroll the body to the matching group header and flash a
  // highlight ring. scrollTo on the body (not scrollIntoView) per design.
  const bodyRef = useRef<HTMLDivElement>(null);
  const blockingHeaderRef = useRef<HTMLDivElement>(null);
  const toCheckHeaderRef = useRef<HTMLDivElement>(null);
  const [highlightedGroup, setHighlightedGroup] = useState<Severity | null>(
    null
  );
  const highlightTimeout = useRef<ReturnType<typeof setTimeout>>();

  const scrollToGroup = useCallback((severity: Severity) => {
    const body = bodyRef.current;
    const header =
      severity === 'warning'
        ? toCheckHeaderRef.current
        : blockingHeaderRef.current;
    if (body && header) {
      // Position relative to the current scroll, via bounding rects: robust to
      // the body's padding and to whichever ancestor is the offsetParent.
      const top =
        body.scrollTop +
        header.getBoundingClientRect().top -
        body.getBoundingClientRect().top;
      body.scrollTo({ top, behavior: 'smooth' });
    }
    setHighlightedGroup(severity);
    if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    highlightTimeout.current = setTimeout(
      () => setHighlightedGroup(null),
      1100
    );
  }, []);

  useEffect(
    () => () => {
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    },
    []
  );

  return (
    <Sidebar
      title={t('components.alert_sidebar.title')}
      onClose={onClose}
      showOverlay
      bodyRef={bodyRef}
    >
      <div className="space-y-4">
        {/* Severity counter tiles (types, not products) */}
        {(hasBlockingContent || hasToCheckContent) && (
          <div className="flex gap-2.5">
            {hasBlockingContent && (
              <AnomalyCountTile
                severity="error"
                count={blockingTypesCount}
                onClick={() => scrollToGroup('error')}
              />
            )}
            {hasToCheckContent && (
              <AnomalyCountTile
                severity="warning"
                count={toCheckTypesCount}
                onClick={() => scrollToGroup('warning')}
              />
            )}
          </div>
        )}

        {/* Global expand / collapse-all control */}
        {allAnomalyIds.length > 0 && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={toggleAll}
              data-testid="alert-sidebar-toggle-all"
              className="flex items-center gap-1 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              {t(
                allOpen
                  ? 'components.alert_sidebar.collapse_all'
                  : 'components.alert_sidebar.expand_all'
              )}
              <ChevronDown
                className={cn(
                  'size-3.5 transition-transform',
                  allOpen && 'rotate-180'
                )}
              />
            </button>
          </div>
        )}

        {/* ---- Group: À corriger avant validation (blocking) ---- */}
        {hasBlockingContent && (
          <div className="p-2">
            <AnomalyGroupHeader
              severity="error"
              headerRef={blockingHeaderRef}
              highlighted={highlightedGroup === 'error'}
              title={t('components.alert_sidebar.group_blocking_title')}
              description={t(
                'components.alert_sidebar.group_blocking_description',
                { count: blockingTypesCount }
              )}
            />

            {/* Generic blocking validation errors: one titled card per rule */}
            {visibleBlockingErrors.map((error) => (
              <AnomalyCollapsible
                key={error.ruleId}
                id={`blocking-error-${error.ruleId}`}
                severity="error"
                dataTestId={`alert-sidebar-blocking-error-${error.ruleId}`}
                title={t(`validation_errors.${error.ruleId}.ruleName`, {
                  defaultValue: error.ruleId,
                })}
                description={blockingErrorDescription(error.ruleId)}
                count={error.count}
                open={openIds.has(`blocking-error-${error.ruleId}`)}
                onToggle={() => toggleId(`blocking-error-${error.ruleId}`)}
              >
                <div className="flex w-full items-center gap-2 rounded-md border p-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[13px] text-gray-400">
                      {t('components.alert_sidebar.products_concerned', {
                        count: error.count,
                      })}
                    </span>
                    <span className="text-[15px] text-gray-900">
                      {error.count}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="ml-auto border-gray-300 font-medium"
                    data-testid={`alert-sidebar-blocking-error-filter-${error.ruleId}`}
                    onClick={() => handleFilterBlockingError(error.ruleId)}
                  >
                    {t('components.alert_sidebar.filter_cta')}
                  </Button>
                </div>
              </AnomalyCollapsible>
            ))}

            {/* Missing category Section (object products, one-click categorize) */}
            {isInternalUser && missingCategoryIds.length > 0 && (
              <AnomalyCollapsible
                id="missing-category"
                severity="error"
                title={t('components.alert_sidebar.category_title')}
                description={t('components.alert_sidebar.category_description')}
                count={missingCategoryIds.length}
                open={openIds.has('missing-category')}
                onToggle={() => toggleId('missing-category')}
              >
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-gray-400">
                        {t('components.alert_sidebar.products_concerned', {
                          count: missingCategoryIds.length,
                        })}
                      </span>
                      <span className="text-[15px] text-gray-900">
                        {missingCategoryIds.length}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="ml-auto border-gray-300 font-medium"
                      data-testid="alert-sidebar-categorize-filter"
                      onClick={handleFilterMissingCategory}
                    >
                      {t('components.alert_sidebar.filter_cta')}
                    </Button>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      type="button"
                      className="font-medium"
                      data-testid="alert-sidebar-categorize"
                      disabled={isCategorizing || syncingShopifyCategory}
                      onClick={handleCategorize}
                    >
                      {isCategorizing || syncingShopifyCategory ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : hasShopifyConnection ? (
                        t('components.alert_sidebar.category_cta_shopify')
                      ) : (
                        t('components.alert_sidebar.category_cta', {
                          count: missingCategoryIds.length,
                        })
                      )}
                    </Button>
                  </div>
                </div>
              </AnomalyCollapsible>
            )}

            {/* Missing shipping Section (object products, one bulk amount) */}
            {isInternalUser && missingShippingIds.length > 0 && (
              <AnomalyCollapsible
                id="missing-shipping"
                severity="error"
                title={t('components.alert_sidebar.shipping_title')}
                description={t('components.alert_sidebar.shipping_description')}
                count={missingShippingIds.length}
                open={openIds.has('missing-shipping')}
                onToggle={() => toggleId('missing-shipping')}
              >
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-gray-400">
                        {t('components.alert_sidebar.products_concerned', {
                          count: missingShippingIds.length,
                        })}
                      </span>
                      <span className="text-[15px] text-gray-900">
                        {missingShippingIds.length}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="ml-auto border-gray-300 font-medium"
                      data-testid="alert-sidebar-shipping-filter"
                      onClick={handleFilterMissingShipping}
                    >
                      {t('components.alert_sidebar.filter_cta')}
                    </Button>
                  </div>

                  {/* Layout reused from the bulk edit "Logistique" shipping field */}
                  <div className="space-y-2">
                    <Label htmlFor="alert-sidebar-shipping-input">
                      {t('kpi.shipping_price')} ({vatLabel}) (
                      {store === Store.FR ? Currency.EUR : Currency.USD})
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="alert-sidebar-shipping-input"
                        type="text"
                        inputMode="decimal"
                        value={shippingValue}
                        onChange={(event) =>
                          setShippingValue(event.target.value)
                        }
                        placeholder="ex: 2.90"
                        className="flex-1"
                        data-testid="alert-sidebar-shipping-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        data-testid="alert-sidebar-shipping-apply"
                        disabled={isApplyingShipping || !isShippingValueValid}
                        onClick={handleApplyShipping}
                      >
                        {isApplyingShipping ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          t('general.modify')
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </AnomalyCollapsible>
            )}

            {/* Duplicate variants Section (object items: Shopify recovery or image extraction) */}
            {isInternalUser && hasDuplicateVariantFix && (
              <AnomalyCollapsible
                id="duplicate-variant"
                severity="error"
                title={t('components.alert_sidebar.variant_title')}
                description={t(variantDescriptionKey)}
                count={
                  duplicateVariantShopifyIds.length +
                  duplicateVariantWithImagesIds.length +
                  duplicateVariantManualIds.length
                }
                open={openIds.has('duplicate-variant')}
                onToggle={() => toggleId('duplicate-variant')}
              >
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-gray-400">
                        {t('components.alert_sidebar.products_concerned', {
                          count:
                            duplicateVariantShopifyIds.length +
                            duplicateVariantWithImagesIds.length +
                            duplicateVariantManualIds.length,
                        })}
                      </span>
                      <span className="text-[15px] text-gray-900">
                        {duplicateVariantShopifyIds.length +
                          duplicateVariantWithImagesIds.length +
                          duplicateVariantManualIds.length}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="ml-auto border-gray-300 font-medium"
                      data-testid="alert-sidebar-variant-filter"
                      onClick={handleFilterDuplicateVariants}
                    >
                      {t('components.alert_sidebar.filter_cta')}
                    </Button>
                  </div>
                  {(canRecoverVariants || extractOptionsIds.length > 0) && (
                    <div className="flex flex-col items-end gap-2">
                      {canRecoverVariants && (
                        <Button
                          type="button"
                          className="font-medium"
                          data-testid="alert-sidebar-recover-variants"
                          disabled={syncingShopifyVariants}
                          onClick={handleRecoverVariants}
                        >
                          {syncingShopifyVariants ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            t('components.alert_sidebar.variant_cta')
                          )}
                        </Button>
                      )}
                      {extractOptionsIds.length > 0 && (
                        <Button
                          type="button"
                          className="font-medium"
                          data-testid="alert-sidebar-extract-variant-options"
                          disabled={isExtractingOptions}
                          onClick={handleExtractOptions}
                        >
                          {isExtractingOptions ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            t('components.alert_sidebar.variant_cta_extract')
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </AnomalyCollapsible>
            )}

            {/* Missing Coupon Codes Section (digital products, blocking) */}
            {hasDigitalProductsWithoutCoupons && (
              <AnomalyCollapsible
                id="missing-coupons"
                severity="error"
                title={t('components.alert_sidebar.missing_coupons_title')}
                count={digitalProductsCount}
                open={openIds.has('missing-coupons')}
                onToggle={() => toggleId('missing-coupons')}
              >
                <div className="flex w-full items-center gap-2 rounded-md border p-2">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[13px] text-gray-400">
                      {t('components.alert_sidebar.products_concerned', {
                        count: digitalProductsCount,
                      })}
                    </span>
                    <span className="text-[15px] text-gray-900">
                      {digitalProductsCount}
                    </span>
                  </div>
                  <Button
                    type="button"
                    className="ml-auto font-medium"
                    data-testid="alert-sidebar-import-coupon-codes"
                    onClick={() => {
                      setOpenImportCouponCodesModal(true);
                      onClose();
                    }}
                  >
                    <span> {t('general.import')}</span>
                  </Button>
                </div>
              </AnomalyCollapsible>
            )}
          </div>
        )}

        {/* ---- Group: À vérifier (non-blocking) ---- */}
        {hasToCheckContent && (
          <div className="p-2">
            <AnomalyGroupHeader
              severity="warning"
              headerRef={toCheckHeaderRef}
              highlighted={highlightedGroup === 'warning'}
              title={t('components.alert_sidebar.group_to_check_title')}
              description={t(
                'components.alert_sidebar.group_to_check_description',
                { count: toCheckTypesCount }
              )}
            />

            {isInternalUser && psychoPriceIds.length > 0 && (
              <AnomalyCollapsible
                id="psycho"
                severity="warning"
                title={t('components.alert_sidebar.psycho_title')}
                description={t('components.alert_sidebar.psycho_description')}
                count={psychoPriceIds.length}
                open={openIds.has('psycho')}
                onToggle={() => toggleId('psycho')}
              >
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-gray-400">
                        {t('components.alert_sidebar.products_concerned', {
                          count: psychoPriceIds.length,
                        })}
                      </span>
                      <span className="text-[15px] text-gray-900">
                        {psychoPriceIds.length}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="ml-auto border-gray-300 font-medium"
                      data-testid="alert-sidebar-psycho-filter"
                      onClick={handleFilterPsychoPrices}
                    >
                      {t('components.alert_sidebar.filter_cta')}
                    </Button>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      type="button"
                      className="font-medium"
                      data-testid="alert-sidebar-psycho-recompute"
                      disabled={isApplyingPsycho}
                      onClick={() => setShowPsychoModal(true)}
                    >
                      {isApplyingPsycho ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('components.alert_sidebar.psycho_cta')
                      )}
                    </Button>
                  </div>
                </div>
              </AnomalyCollapsible>
            )}

            {/* Invalid GTIN Section (object items that still have a SKU or Shopify id) */}
            {isInternalUser && invalidGtinIds.length > 0 && (
              <AnomalyCollapsible
                id="invalid-gtin"
                severity="warning"
                title={t('components.alert_sidebar.gtin_title')}
                description={t('components.alert_sidebar.gtin_description')}
                count={invalidGtinIds.length}
                open={openIds.has('invalid-gtin')}
                onToggle={() => toggleId('invalid-gtin')}
              >
                <div className="flex flex-col gap-2 rounded-md border p-2">
                  <div className="flex w-full items-center gap-2">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-[13px] text-gray-400">
                        {t('components.alert_sidebar.products_concerned', {
                          count: invalidGtinIds.length,
                        })}
                      </span>
                      <span className="text-[15px] text-gray-900">
                        {invalidGtinIds.length}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="ml-auto border-gray-300 font-medium"
                      data-testid="alert-sidebar-invalid-gtin-filter"
                      onClick={handleFilterInvalidGtins}
                    >
                      {t('components.alert_sidebar.filter_cta')}
                    </Button>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      type="button"
                      className="font-medium"
                      data-testid="alert-sidebar-remove-invalid-gtins"
                      disabled={isRemovingGtins}
                      onClick={() => setShowRemoveGtinDialog(true)}
                    >
                      {isRemovingGtins ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t('components.alert_sidebar.gtin_remove_cta')
                      )}
                    </Button>
                  </div>
                </div>
              </AnomalyCollapsible>
            )}

            {/* VAT Alerts Section (multiple VAT rates → to check) */}
            {uniqueVatRates.length > 1 && (
              <AnomalyCollapsible
                id="vat-rates"
                severity="warning"
                count={vatTotalCount}
                open={openIds.has('vat-rates')}
                onToggle={() => toggleId('vat-rates')}
                title={t('components.alert_sidebar.vat_rates_detected')}
                description={t(
                  'components.alert_sidebar.vat_rates_description',
                  {
                    count: uniqueVatRates.length,
                    vatRates: uniqueVatRates.join(', '),
                  }
                )}
              >
                <div className="flex gap-2">
                  <div className="flex w-full flex-col gap-2">
                    {uniqueVatRates.map((rate) => (
                      <div
                        key={rate}
                        className="flex w-full items-center gap-2 rounded-md border p-2"
                      >
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[13px] text-gray-400">
                            {t(
                              'components.alert_sidebar.vat_rates_products_count',
                              {
                                vatRate: rate,
                                count: countProductsByVatRate[rate] ?? 0,
                              }
                            )}
                          </span>
                          <span className="text-[15px] text-gray-900">
                            {countProductsByVatRate[rate]}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          className="ml-auto border-gray-300 font-medium"
                          data-testid={`alert-sidebar-vat-rate-check-${rate}`}
                          onClick={() => handleVatRateCheck(rate)}
                        >
                          {t('components.alert_sidebar.vat_rates_check')}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </AnomalyCollapsible>
            )}
          </div>
        )}
      </div>

      <ApplyPsychoPricesDialog
        open={showPsychoModal}
        onOpenChange={setShowPsychoModal}
        onSelectStrategy={applyPsychoPrices}
      />

      <RemoveInvalidGtinsDialog
        open={showRemoveGtinDialog}
        onOpenChange={setShowRemoveGtinDialog}
        count={invalidGtinIds.length}
        onConfirm={handleRemoveInvalidGtins}
      />
    </Sidebar>
  );
};
